import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import DeviceSyncPanel from "@/components/DeviceSyncPanel";
import { useT } from "@/lib/i18n";
import { TEST } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { IndianRupee, Users, Beef, TrendingUp, Building2, Wallet, ShoppingBag, Clock, ShieldCheck, ClipboardList } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="am-card p-6" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{label}</span>
        <div className="w-10 h-10 rounded-2xl bg-accent grid place-items-center"><Icon className="w-5 h-5 text-primary" /></div>
      </div>
      <div className="mt-4 font-display text-3xl font-extrabold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useT();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  if (!stats) return <DashboardShell title="…" />;

  const fmtINR = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <DashboardShell title={t("admin.platformOverview")} subtitle="AnimalMela — Super Admin">
      <div className="flex flex-wrap gap-2 mb-5 no-print">
        <Link to="/admin/records"><Button className="am-btn-primary rounded-full" data-testid="admin-records-cta"><ClipboardList className="w-4 h-4 mr-1.5" /> Records & Controls</Button></Link>
        <Link to="/admin/subscribers"><Button variant="outline" className="rounded-full"><Building2 className="w-4 h-4 mr-1.5" /> Subscribers</Button></Link>
        <Link to="/admin/tickets"><Button variant="outline" className="rounded-full"><ShieldCheck className="w-4 h-4 mr-1.5" /> Tickets</Button></Link>
        <Link to="/admin/plans"><Button variant="outline" className="rounded-full"><IndianRupee className="w-4 h-4 mr-1.5" /> Plans</Button></Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat icon={Building2} label="Subscribers" value={stats.total_subscribers} sub={`${stats.active_subscribers} active`} testId={TEST.adminStatSubs} />
        <Stat icon={Users} label="Helpers" value={stats.total_helpers.toLocaleString("en-IN")} />
        <Stat icon={ShoppingBag} label="Sellers" value={stats.total_sellers.toLocaleString("en-IN")} />
        <Stat icon={ShoppingBag} label="Purchasers" value={stats.total_purchasers.toLocaleString("en-IN")} />
        <Stat icon={Beef} label="Animals" value={stats.total_animals.toLocaleString("en-IN")} />
        <Stat icon={Wallet} label="Collections" value={stats.total_collections?.toLocaleString("en-IN") || 0} />
        <Stat icon={ShieldCheck} label="Active subs" value={stats.active_subscribers} sub={`MRR ${fmtINR(stats.mrr)}`} testId={TEST.adminStatRevenue} />
        <Stat icon={Clock} label="Expiring ≤ 7d" value={stats.expiring_subscriptions ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
        <div className="am-card p-6 lg:col-span-2">
          <h2 className="font-display text-xl font-bold mb-4">{t("admin.planDist")}</h2>
          <div className="space-y-4">
            {Object.entries(stats.plan_breakdown).map(([pid, count]) => {
              const total = Object.values(stats.plan_breakdown).reduce((a, b) => a + b, 0) || 1;
              const pct = (count / total) * 100;
              return (
                <div key={pid}>
                  <div className="flex justify-between text-sm font-semibold mb-1.5">
                    <span className="capitalize">{pid}</span>
                    <span className="font-mono">{count}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="am-card p-6">
          <h2 className="font-display text-xl font-bold mb-4">{t("admin.quickStats")}</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex justify-between"><span className="text-muted-foreground">{t("admin.sellersOnboarded")}</span><span className="font-mono font-bold">{stats.total_sellers}</span></li>
            <li className="flex justify-between"><span className="text-muted-foreground">{t("admin.purchasersOnboarded")}</span><span className="font-mono font-bold">{stats.total_purchasers}</span></li>
            <li className="flex justify-between"><span className="text-muted-foreground">{t("admin.openTickets")}</span><span className="font-mono font-bold">{stats.open_tickets}</span></li>
          </ul>
        </div>
      </div>

      <div className="am-card p-6 mt-6">
        <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" /> {t("admin.latestSubs")}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                <th className="pb-3 pr-4">{t("admin.haat")}</th>
                <th className="pb-3 pr-4">{t("admin.owner")}</th>
                <th className="pb-3 pr-4">{t("admin.plan")}</th>
                <th className="pb-3 pr-4">{t("admin.location")}</th>
                <th className="pb-3">{t("common.status")}</th>
              </tr>
            </thead>
            <tbody>
              {subs.slice(0, 8).map((s) => (
                <tr key={s.subscriber_id} className="border-b last:border-0">
                  <td className="py-3 pr-4 font-semibold">{s.haat_name}</td>
                  <td className="py-3 pr-4">{s.owner_name} <span className="text-muted-foreground font-mono text-xs">{s.owner_mobile}</span></td>
                  <td className="py-3 pr-4 capitalize">{s.plan_id}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{[s.village, s.district, s.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="py-3">
                    <span className={`am-chip ${s.is_active ? "" : "bg-muted text-muted-foreground"}`}>
                      {s.is_active ? t("common.active") : t("common.disabled")}
                    </span>
                  </td>
                </tr>
              ))}
              {subs.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t("sub.noData")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeviceSyncPanel basePath="/admin" />
    </DashboardShell>
  );
}
